import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { clientsAPI, sitesAPI } from '../utils/api';
import { toast } from 'sonner';

export function CreateSiteModal({ open, onClose, onCreated, defaultClientId }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    name: '',
    client_id: defaultClientId || '',
    location: '',
    latitude: '',
    longitude: '',
    installed_power_kw: '',
    installed_capacity_kwh: '',
    status: 'offline',
    commissioned_date: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clientsAPI.getAll().then(r => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (defaultClientId) setForm(f => ({ ...f, client_id: defaultClientId }));
  }, [defaultClientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.name || !form.location || !form.installed_power_kw || !form.installed_capacity_kwh) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        installed_power_kw: parseFloat(form.installed_power_kw),
        installed_capacity_kwh: parseFloat(form.installed_capacity_kwh),
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        commissioned_date: form.commissioned_date || null,
      };
      const res = await sitesAPI.create(payload);
      toast.success('Site created');
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create site');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Create Site</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-3">
          <div>
            <Label>Client *</Label>
            <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
              <SelectTrigger className="mt-1" data-testid="site-client-select">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => (
                  <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Site Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. 250KW 500kWh - Location" className="mt-1" data-testid="site-name-input" required />
          </div>
          <div>
            <Label>Location *</Label>
            <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="City, State" className="mt-1" data-testid="site-location-input" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Latitude</Label>
              <Input type="number" step="any" value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })} className="mt-1" data-testid="site-lat-input" />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input type="number" step="any" value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })} className="mt-1" data-testid="site-lng-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Power (kW) *</Label>
              <Input type="number" step="any" value={form.installed_power_kw} onChange={e => setForm({ ...form, installed_power_kw: e.target.value })} className="mt-1" data-testid="site-power-input" required />
            </div>
            <div>
              <Label>Capacity (kWh) *</Label>
              <Input type="number" step="any" value={form.installed_capacity_kwh} onChange={e => setForm({ ...form, installed_capacity_kwh: e.target.value })} className="mt-1" data-testid="site-capacity-input" required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Initial Status</Label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="site-status-select">
                <option value="offline">Offline</option>
                <option value="online">Online</option>
                <option value="partial_offline">Partial Offline</option>
              </select>
            </div>
            <div>
              <Label>Commissioned Date</Label>
              <Input type="date" value={form.commissioned_date} onChange={e => setForm({ ...form, commissioned_date: e.target.value })} className="mt-1" data-testid="site-date-input" />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-3 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="create-site-submit">
              {saving ? 'Creating...' : 'Create Site'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
